import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, orderBy, getDocs, limit, startAfter } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import ForumLeftContent from '../../partials/community/ForumLeftContent';
import ForumEntries from '../../partials/community/ForumEntries';
import ForumRightContent from '../../partials/community/ForumRightContent';

function Forum() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('popular');
  const [posts, setPosts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [followedStocks, setFollowedStocks] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const { user } = useAuth();
  const db = getFirestore();
  const postsPerPage = 9;

  // Fetch followed stocks (unchanged)
  useEffect(() => {
    const fetchFollowedStocks = async () => {
      // ... (unchanged code)
    };

    fetchFollowedStocks();
  }, [user, db]);

  // Fetch posts
  const fetchPosts = async (isInitial = false) => {
    if (!user && (activeFilter === 'liked' || activeFilter === 'my-posts' || activeFilter === 'following')) {
      setPosts([]);
      return;
    }

    const postsRef = collection(db, 'posts');
    let q;

    switch (activeFilter) {
      case 'liked':
        // ... (unchanged code)
        break;

      case 'my-posts':
        q = query(postsRef, where('authorId', '==', user.uid), orderBy('timestamp', 'desc'), limit(postsPerPage));
        break;

      case 'popular':
        q = query(postsRef, orderBy('upvotes', 'desc'), limit(postsPerPage));
        break;

      case 'newest':
        q = query(postsRef, orderBy('timestamp', 'desc'), limit(postsPerPage));
        break;

      case 'following':
        if (followedStocks.length === 0) {
          setPosts([]);
          return;
        }
        const followedSymbols = followedStocks.map(stock => stock.symbol);
        q = query(postsRef, where('stockSymbol', 'in', followedSymbols), orderBy('timestamp', 'desc'), limit(postsPerPage));
        break;

      default:
        q = query(postsRef, orderBy('timestamp', 'desc'), limit(postsPerPage));
    }

    if (!isInitial && lastVisible) {
      q = query(q, startAfter(lastVisible));
    }

    try {
      const snapshot = await getDocs(q);
      const newPosts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (isInitial) {
        setPosts(newPosts);
      } else {
        setPosts(prevPosts => [...prevPosts, ...newPosts]);
      }

      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === postsPerPage);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };

  useEffect(() => {
    fetchPosts(true);
  }, [activeFilter, user, db, followedStocks]);

  const loadMorePosts = () => {
    fetchPosts();
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto">
            <div className="flex justify-between items-center mb-4 pt-8">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Forum</h1>
              <button
                onClick={() => setModalOpen(true)}
                className="btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
              >
                Opret indlæg
              </button>
            </div>

            <div className="xl:flex">
              <div className="md:flex flex-1">
                <ForumLeftContent setActiveFilter={setActiveFilter} />

                <div className="flex-1 md:ml-8 xl:mx-4 2xl:mx-8">
                  <div className="md:py-8">
                    <div className="mb-4">
                      <div className="w-full flex flex-wrap -space-x-px">
                        {/* ... (unchanged filter buttons) */}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <ForumEntries 
                        filter={activeFilter} 
                        posts={posts}
                        followedStocks={followedStocks}
                      />
                    </div>

                    {hasMore && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={loadMorePosts}
                          className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                        >
                          Indlæs flere indlæg
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <ForumRightContent modalOpen={modalOpen} setModalOpen={setModalOpen} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Forum;
